import { useCallback, useEffect, useState } from 'react';
import { useEscapeToClose } from '../utils/useEscapeToClose';
import { FiX, FiSlash, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import { apiRequest } from '../utils/auth';

export type ReportType = 'extension' | 'creator';

const REASON_LABELS: Record<string, string> = {
  inappropriate_content: 'Inappropriate content',
  spam: 'Spam',
  copyright_violation: 'Copyright',
  misleading: 'Misleading',
  harassment: 'Harassment',
  other: 'Other',
};

function reasonLabel(reason: string): string {
  return REASON_LABELS[reason] || reason;
}

function formatDateTime(value?: string): string {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface ReportItem {
  _id: string;
  userId: string;
  reporterName: string;
  reporterEmail: string | null;
  reason: string;
  additionalInfo: string | null;
  createdAt: string;
}

interface ExtensionSummary {
  extensionId: string;
  name: string;
  isDeleted: boolean;
  reportStatus: string;
  creatorId: string | null;
  creatorName: string | null;
  creatorProfilePicture: string | null;
  thumbnailUrl?: string;
}

interface CreatorSummary {
  creatorId: string;
  creatorName: string;
  creatorProfilePicture: string | null;
  isCreatorDisabled: boolean;
  violationType: string | null;
}

interface ReportDetailsModalProps {
  type: ReportType;
  id: string;
  isOpen: boolean;
  onClose: () => void;
  onActionDone: () => void;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px',
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: '12px',
  width: '100%',
  maxWidth: '720px',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

function StatusBadge({ label, color }: { label: string; color: string }) {
  return (
    <span style={{ padding: '3px 10px', borderRadius: '6px', backgroundColor: color, color: 'white', fontSize: '12px', fontWeight: 600 }}>
      {label}
    </span>
  );
}

export default function ReportDetailsModal({ type, id, isOpen, onClose, onActionDone }: ReportDetailsModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [totalReports, setTotalReports] = useState(0);
  const [reasonBreakdown, setReasonBreakdown] = useState<Record<string, number>>({});
  const [extension, setExtension] = useState<ExtensionSummary | null>(null);
  const [creator, setCreator] = useState<CreatorSummary | null>(null);

  const [reasonText, setReasonText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const endpoint =
        type === 'extension'
          ? `/api/admin/reports/extensions/${id}?limit=100`
          : `/api/admin/reports/creators/${id}?limit=100`;
      const response = await apiRequest<{
        success: boolean;
        extension?: ExtensionSummary;
        creator?: CreatorSummary;
        totalReports: number;
        reasonBreakdown: Record<string, number>;
        reports: ReportItem[];
        error?: string;
      }>(endpoint);
      if (response.success) {
        setReports(response.reports || []);
        setTotalReports(response.totalReports || 0);
        setReasonBreakdown(response.reasonBreakdown || {});
        setExtension(response.extension || null);
        setCreator(response.creator || null);
      } else {
        setError(response.error || 'Failed to load report details');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report details');
    } finally {
      setLoading(false);
    }
  }, [type, id]);

  useEffect(() => {
    if (isOpen) {
      setReasonText('');
      setActionError(null);
      fetchDetail();
    }
  }, [isOpen, fetchDetail]);

  // Escape dismisses the modal, same as the × button and the backdrop click.
  useEscapeToClose(isOpen, onClose);

  if (!isOpen) return null;

  const isSuspended = type === 'extension' ? extension?.reportStatus === 'Suspended' : !!creator?.isCreatorDisabled;
  const isDeletedTarget = type === 'extension' && extension?.isDeleted;
  const displayName = type === 'extension' ? extension?.name : creator?.creatorName;
  const avatarUrl = type === 'extension' ? extension?.thumbnailUrl : creator?.creatorProfilePicture;

  const runAction = async (action: 'suspend' | 'unsuspend' | 'ban' | 'unban') => {
    setActionError(null);
    const needsReason = action === 'suspend' || action === 'ban';
    if (needsReason && !reasonText.trim()) {
      setActionError('Please enter a reason.');
      return;
    }
    try {
      setActionLoading(true);
      let endpoint = '';
      const body: Record<string, string> = {};
      if (type === 'extension') {
        endpoint = `/api/admin/extensions/${id}/${action === 'suspend' ? 'suspend' : 'unsuspend'}`;
        if (action === 'suspend') body.reason = reasonText.trim();
      } else {
        endpoint = `/api/admin/users/${id}/${action === 'ban' ? 'ban-creator' : 'unban-creator'}`;
        if (action === 'ban') body.reason = reasonText.trim();
      }
      const response = await apiRequest<{ success: boolean; error?: string }>(endpoint, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (response.success) {
        setReasonText('');
        await fetchDetail();
        onActionDone();
      } else {
        setActionError(response.error || 'Action failed');
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '18px 20px', borderBottom: '1px solid var(--color-border)' }}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              style={{ width: 48, height: 48, borderRadius: type === 'extension' ? '8px' : '50%', objectFit: 'cover', flexShrink: 0 }}
            />
          ) : (
            <div style={{ width: 48, height: 48, borderRadius: type === 'extension' ? '8px' : '50%', backgroundColor: 'var(--color-border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-muted)', fontWeight: 700 }}>
              {(displayName || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '17px', fontWeight: 700, color: 'var(--color-text)' }}>{displayName || '-'}</span>
              {type === 'extension' ? (
                extension?.reportStatus === 'Suspended' ? (
                  <StatusBadge label="Suspended" color="var(--color-danger-strong)" />
                ) : extension?.reportStatus === 'inReview' ? (
                  <StatusBadge label="In Review" color="var(--color-warning-border)" />
                ) : isDeletedTarget ? (
                  <StatusBadge label="Deleted" color="var(--color-subtle)" />
                ) : (
                  <StatusBadge label="Active" color="var(--color-success-strong)" />
                )
              ) : creator?.isCreatorDisabled ? (
                <StatusBadge label="Banned" color="var(--color-danger-strong)" />
              ) : (
                <StatusBadge label="Active" color="var(--color-success-strong)" />
              )}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--color-muted)', marginTop: '2px' }}>
              {type === 'extension'
                ? `Creator: ${extension?.creatorName || '-'}`
                : `Creator ID: ${creator?.creatorId || id}`}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', padding: '4px' }} aria-label="Close">
            <FiX size={22} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)' }}>Loading report details…</div>
          ) : error ? (
            <div style={{ padding: '12px', backgroundColor: 'var(--color-danger-surface)', color: 'var(--color-danger)', borderRadius: '6px' }}>{error}</div>
          ) : (
            <>
              {/* Summary */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <span style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-text)' }}>
                  {totalReports} report{totalReports === 1 ? '' : 's'} total
                </span>
                {Object.entries(reasonBreakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([reason, count]) => (
                    <span key={reason} style={{ padding: '3px 9px', borderRadius: '12px', backgroundColor: 'var(--color-surface-hover)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', fontSize: '12px' }}>
                      {reasonLabel(reason)} · {count}
                    </span>
                  ))}
              </div>

              {/* Moderation action */}
              <div style={{ backgroundColor: 'var(--color-input)', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
                {isSuspended ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                    <span style={{ color: 'var(--color-muted)', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <FiAlertTriangle size={14} color="var(--color-danger-strong)" />
                      {type === 'extension'
                        ? 'This lens is currently suspended.'
                        : `Creator is banned${creator?.violationType ? ` (${creator.violationType})` : ''}.`}
                    </span>
                    <button
                      onClick={() => runAction(type === 'extension' ? 'unsuspend' : 'unban')}
                      disabled={actionLoading}
                      style={{ padding: '8px 16px', backgroundColor: 'var(--color-success-strong)', color: 'white', border: 'none', borderRadius: '6px', cursor: actionLoading ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: actionLoading ? 0.6 : 1 }}
                    >
                      <FiCheckCircle size={14} /> {type === 'extension' ? 'Unsuspend lens' : 'Unban creator'}
                    </button>
                  </div>
                ) : isDeletedTarget ? (
                  <span style={{ color: 'var(--color-muted)', fontSize: '13px' }}>This lens no longer exists — nothing to suspend.</span>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <label style={{ fontSize: '13px', color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                      {type === 'extension' ? 'Suspend this lens' : 'Ban this creator'}
                    </label>
                    <textarea
                      value={reasonText}
                      onChange={(e) => setReasonText(e.target.value)}
                      placeholder="Reason (shown in the moderation history)…"
                      rows={2}
                      style={{ width: '100%', padding: '8px 10px', backgroundColor: 'var(--color-input)', border: '1px solid var(--color-border)', borderRadius: '6px', color: 'var(--color-text)', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                    <button
                      onClick={() => runAction(type === 'extension' ? 'suspend' : 'ban')}
                      disabled={actionLoading}
                      style={{ alignSelf: 'flex-start', padding: '8px 16px', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', borderRadius: '6px', cursor: actionLoading ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: actionLoading ? 0.6 : 1 }}
                    >
                      <FiSlash size={14} /> {type === 'extension' ? 'Suspend lens' : 'Ban creator'}
                    </button>
                  </div>
                )}
                {actionError && (
                  <div style={{ marginTop: '10px', color: 'var(--color-danger)', fontSize: '12px' }}>{actionError}</div>
                )}
              </div>

              {/* Individual reports */}
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-muted)', marginBottom: '10px' }}>
                Individual reports{reports.length < totalReports ? ` (showing ${reports.length} of ${totalReports})` : ''}
              </div>
              {reports.length === 0 ? (
                <div style={{ color: 'var(--color-subtle)', fontSize: '13px' }}>No reports.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {reports.map((r) => (
                    <div key={r._id} style={{ backgroundColor: 'var(--color-input)', border: '1px solid var(--color-surface-hover)', borderRadius: '8px', padding: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{ padding: '2px 8px', borderRadius: '10px', backgroundColor: '#1c2b3a', color: 'var(--color-accent)', fontSize: '11px', fontWeight: 600 }}>
                            {reasonLabel(r.reason)}
                          </span>
                          <span style={{ color: 'var(--color-text-secondary)', fontSize: '13px' }}>{r.reporterName}</span>
                          {r.reporterEmail && <span style={{ color: 'var(--color-subtle)', fontSize: '12px' }}>{r.reporterEmail}</span>}
                        </span>
                        <span style={{ color: 'var(--color-subtle)', fontSize: '12px' }}>{formatDateTime(r.createdAt)}</span>
                      </div>
                      {r.additionalInfo && (
                        <div style={{ marginTop: '8px', color: 'var(--color-muted)', fontSize: '13px', whiteSpace: 'pre-wrap' }}>{r.additionalInfo}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
